import { z } from 'zod';
import { apiClient } from '@/lib/api/client';
import {
  OrganizationListSchema,
  OrganizationSchema,
  OrganizationPermissionsSchema,
  ProjectPermissionsSchema,
  PermissionsResponseSchema,
  OrganizationMembersListSchema,
  CreateOrganizationSchema,
  UpdateOrganizationSchema,
  type Organization,
  type OrganizationList,
  type OrganizationPermissions,
  type ProjectPermissions,
  type PermissionsResponse,
  type OrganizationMembersList,
  type CreateOrganization,
  type UpdateOrganization,
  type OrganizationQueryParams,
} from './organization.schemas';

// ============================================================================
// Organization API Functions
// ============================================================================

/**
 * Fetch all organizations for the current user
 */
export async function getOrganizations(params?: OrganizationQueryParams): Promise<OrganizationList> {
  const response = await apiClient.get('/organizations', { params });
  return OrganizationListSchema.parse(response.data);
}

/**
 * Fetch a specific organization by ID
 */
export async function getOrganization(organizationId: string): Promise<Organization> {
  const response = await apiClient.get(`/organizations/${organizationId}`);
  return OrganizationSchema.parse(response.data);
}

/**
 * Lightweight org details fetch used to hydrate display-only fields
 * (name, timestamps) on top of the session-derived membership shape.
 *
 * The BFF returns the org-manager-api payload directly, which uses
 * camelCase keys (`createdAt`, `updatedAt`) and does NOT include the
 * caller's role or membership id — those come from the Kratos session.
 * Strict `OrganizationSchema.parse(...)` rejects that payload, so we
 * use a permissive parse here and re-export only the fields the
 * dashboard actually consumes for display.
 *
 * Display name resolution: `businessInformation.organizationName`
 * (e.g. "WYA Solutions") is the human-friendly label used across the
 * app; the top-level `name` (e.g. "wya") is a short slug. We fall
 * back to `name` if the rich field is missing.
 */
const OrganizationDetailsSchema = z
  .object({
    name: z.string(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    businessInformation: z
      .object({
        organizationName: z.string().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();

export interface OrganizationDetails {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

export async function getOrganizationDetails(
  organizationId: string,
): Promise<OrganizationDetails> {
  const response = await apiClient.get(`/organizations/${organizationId}`);
  const parsed = OrganizationDetailsSchema.parse(response.data);
  return {
    id: organizationId,
    name: parsed.businessInformation?.organizationName || parsed.name,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
  };
}

/**
 * Create a new organization
 */
export async function createOrganization(data: CreateOrganization): Promise<Organization> {
  const validatedData = CreateOrganizationSchema.parse(data);
  const response = await apiClient.post('/organizations', validatedData);
  return OrganizationSchema.parse(response.data);
}

/**
 * Update an existing organization
 */
export async function updateOrganization(
  organizationId: string,
  data: UpdateOrganization
): Promise<Organization> {
  const validatedData = UpdateOrganizationSchema.parse(data);
  const response = await apiClient.patch(`/organizations/${organizationId}`, validatedData);
  return OrganizationSchema.parse(response.data);
}

/**
 * Delete an organization
 */
export async function deleteOrganization(organizationId: string): Promise<void> {
  await apiClient.delete(`/organizations/${organizationId}`);
}

// ============================================================================
// Organization Permissions API Functions
// ============================================================================

/**
 * Fetch combined organization and project permissions for the current user
 * This matches the backend endpoint: /organizations/{orgId}/members/{userId}/permissions
 */
export async function getUserPermissions(
  organizationId: string,
  userId: string
): Promise<{ orgPermissions: OrganizationPermissions; projects: Array<{ id: string; permissions: ProjectPermissions }> }> {
  const response = await apiClient.get(`/organizations/${organizationId}/members/${userId}/permissions`);
  
  // Parse the response
  const orgPermissions = OrganizationPermissionsSchema.parse(response.data.orgPermissions);
  const projects = response.data.projects.map((project: any) => ({
    id: project.id,
    permissions: ProjectPermissionsSchema.parse(project.permissions),
  }));
  
  return { orgPermissions, projects };
}

/**
 * Fetch organization-level permissions for the current user
 * @deprecated Use getUserPermissions instead
 */
export async function getOrganizationPermissions(organizationId: string): Promise<OrganizationPermissions> {
  const response = await apiClient.get(`/organizations/${organizationId}/permissions`);
  return OrganizationPermissionsSchema.parse(response.data);
}

/**
 * Fetch project-level permissions for the current user
 * @deprecated Use getUserPermissions instead
 */
export async function getProjectPermissions(
  organizationId: string,
  projectId: string
): Promise<ProjectPermissions> {
  const response = await apiClient.get(`/organizations/${organizationId}/projects/${projectId}/permissions`);
  return ProjectPermissionsSchema.parse(response.data);
}

/**
 * Fetch combined organization and project permissions
 */
export async function getPermissions(
  organizationId: string,
  projectId?: string
): Promise<PermissionsResponse> {
  const url = projectId
    ? `/organizations/${organizationId}/projects/${projectId}/permissions/combined`
    : `/organizations/${organizationId}/permissions/combined`;
  
  const response = await apiClient.get(url);
  return PermissionsResponseSchema.parse(response.data);
}

// ============================================================================
// Organization Members API Functions
// ============================================================================

/**
 * Fetch members of an organization
 */
export async function getOrganizationMembers(organizationId: string): Promise<OrganizationMembersList> {
  const response = await apiClient.get(`/organizations/${organizationId}/members`);
  return OrganizationMembersListSchema.parse(response.data);
}

/**
 * Invite a new member to an organization
 */
export async function inviteOrganizationMember(
  organizationId: string,
  data: { email: string; role: 'administrator' | 'developer' | 'auditor' }
): Promise<void> {
  await apiClient.post(`/organizations/${organizationId}/members/invite`, data);
}

/**
 * Update a member's role in an organization
 */
export async function updateOrganizationMemberRole(
  organizationId: string,
  memberId: string,
  role: 'owner' | 'administrator' | 'developer' | 'auditor'
): Promise<void> {
  await apiClient.patch(`/organizations/${organizationId}/members/${memberId}`, { role });
}

/**
 * Remove a member from an organization
 */
export async function removeOrganizationMember(
  organizationId: string,
  memberId: string
): Promise<void> {
  await apiClient.delete(`/organizations/${organizationId}/members/${memberId}`);
}

// ============================================================================
// Organization Context API Functions
// ============================================================================

/**
 * Switch organization context for the current user session
 */
export async function switchOrganizationContext(organizationId: string): Promise<void> {
  await apiClient.post('/user/context/organization', { organization_id: organizationId });
}

/**
 * Get current organization context
 */
export async function getCurrentOrganizationContext(): Promise<{ organization_id: string | null }> {
  const response = await apiClient.get('/user/context/organization');
  return response.data;
}